<template>
  <div class="empty-state" :class="classes">
    <div v-if="icon" class="empty-state__icon">
      <i :class="icon"></i>
    </div>
    <img
      v-else-if="image"
      class="empty-state__image"
      :src="image"
      :srcset="imageSrcset || undefined"
      alt=""
    />
    <div class="empty-state__title">{{ title }}</div>
    <p v-if="description" class="empty-state__description">
      {{ description }}
    </p>
    <div v-if="$slots.default" class="empty-state__actions">
      <slot />
    </div>
    <!--
      Escape hatch for markup that must live inside the component's root but
      outside the centered content, such as modals triggered by the actions.
    -->
    <slot name="outside" />
  </div>
</template>

<script>
/**
 * A shared "nothing here yet" state. It covers the icon-led, illustration-led
 * and bare title + description variations that were previously implemented
 * separately in the dashboard, workspace and trash screens.
 */
export default {
  name: 'EmptyState',
  props: {
    /**
     * The title explaining what is empty. Required, it is the one part every
     * empty state has.
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Optional icon class, for example `iconoir-book-stack`. Mutually
     * exclusive with `image`, the icon wins when both are provided.
     */
    icon: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Optional illustration source. Use `imageSrcset` alongside it to provide
     * the high density variant.
     */
    image: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Optional `srcset` for the illustration, typically the `@2x` asset.
     */
    imageSrcset: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Optional supporting text shown underneath the title.
     */
    description: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Draws a dashed border around the empty state so it reads as a
     * placeholder for the content that is missing.
     */
    bordered: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Stretches the empty state to fill the height of its parent.
     */
    fullHeight: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    classes() {
      return {
        'empty-state--bordered': this.bordered,
        'empty-state--full-height': this.fullHeight,
      }
    },
  },
}
</script>
